import type { InitializeAssignUserPlacementRequest, User } from '@bliks-io/contracts';
import { serializeRowData } from './classifyAnchorDrafts';
import { validateUserRow } from './draftRowValidation';
import { nonEditableMembers } from './existingToDrafts';
import { isClearedAnchorDraft } from './flushBaselines';
import type { ParsedUser } from './excelParsers';

/** The shared "this step has no protected rows" value — never mutated. */
export const NO_PROTECTED_IDS: ReadonlySet<string> = new Set();

/**
 * Members the add-users pipeline may never create/update/remove (fails the
 * backend's REPLACEABLE_USER_FILTER): they render as locked-identity rows in
 * the Mitarbeiter table and commit their department/role via the dedicated
 * placement PATCH instead.
 */
export function protectedUserIds(members: User[], usersDone: boolean): Set<string> {
  return new Set(nonEditableMembers(members, usersDone).map((member) => member._id));
}

export function isProtectedRow<TRow extends { entityId?: string }>(
  row: TRow,
  protectedIds: ReadonlySet<string>
): row is TRow & { entityId: string } {
  return row.entityId !== undefined && protectedIds.has(row.entityId);
}

/**
 * selectProtectedIds and the placement commit form ONE lane: a spec that
 * partitions protected rows but has no commit callback configured would
 * silently drop the user's placement edits on the floor. Failing loudly turns
 * that mis-wiring into an immediately visible bug instead.
 */
export function requireProtectedCommit<TCommit>(
  commit: TCommit | undefined,
  pendingRowCount: number
): TCommit {
  if (commit === undefined) {
    throw new Error(
      `${pendingRowCount} protected row(s) need a placement commit, but no protected-commit callback is configured for this step`
    );
  }
  return commit;
}

export interface ProtectedRowPartition<TRow> {
  /** Rows the classify/add-users pipeline may see. */
  editableRows: TRow[];
  /** The baseline with protected rows removed — the classify counterpart. */
  editableBaseline: TRow[];
  /** Protected rows whose data differs from their reference snapshot — each commits one placement PATCH. */
  changedProtectedRows: Array<TRow & { entityId: string }>;
}

/**
 * Splits a draft into the add-users lane and the placement lane BEFORE
 * classifyAnchorDrafts runs — protected rows must never appear in
 * newRows/updatedRows/removedIds (the backend rejects them wholesale).
 * A protected row counts as changed against the flush baseline when one
 * exists (it holds rows-as-sent), else against the server seed snapshot.
 */
export function partitionProtectedRows<TRow extends { data: object; entityId?: string }>(
  draftRows: readonly TRow[],
  baseline: readonly TRow[] | undefined,
  seedRows: readonly TRow[],
  protectedIds: ReadonlySet<string>
): ProtectedRowPartition<TRow> {
  const referenceById = new Map<string, TRow>();
  // Baseline entries win over seeds: they carry what was last sent.
  for (const row of [...seedRows, ...(baseline ?? [])]) {
    if (row.entityId !== undefined) referenceById.set(row.entityId, row);
  }

  const editableRows: TRow[] = [];
  const changedProtectedRows: Array<TRow & { entityId: string }> = [];
  for (const row of draftRows) {
    if (!isProtectedRow(row, protectedIds)) {
      editableRows.push(row);
      continue;
    }
    const reference = referenceById.get(row.entityId);
    if (!reference || serializeRowData(row.data) !== serializeRowData(reference.data)) {
      changedProtectedRows.push(row);
    }
  }

  const editableBaseline = (baseline ?? []).filter((row) => !isProtectedRow(row, protectedIds));
  return { editableRows, editableBaseline, changedProtectedRows };
}

/**
 * Ensures every protected seed row is present in the given row set (matched
 * by entityId). Existing rows keep their object identity — the autosave id
 * stamping and the post-submit reset both match rows by reference.
 */
export function withProtectedRows<TRow extends { entityId?: string; rowNumber: number }>(
  rows: readonly TRow[],
  protectedSeedRows: readonly TRow[]
): TRow[] {
  const presentIds = new Set(rows.map((row) => row.entityId).filter(Boolean));
  const missing = protectedSeedRows.filter(
    (row) => row.entityId !== undefined && !presentIds.has(row.entityId)
  );
  if (missing.length === 0) return [...rows];
  const maxRowNumber = rows.reduce((max, row) => Math.max(max, row.rowNumber), 0);
  return [
    ...rows,
    ...missing.map((row, index) => ({ ...row, rowNumber: maxRowNumber + index + 1 }))
  ];
}

/**
 * The protected rows a draft surfaces: seed-derived, but a flush-baseline
 * entry wins by entityId — the baseline holds rows-as-sent, which is newer
 * than a seed snapshot still awaiting its refetch. Without this, "Liste
 * leeren" (or an empty-draft adoption) inside that window would resurrect the
 * stale seed placement and the next flush would PATCH it back server-side.
 */
function resolveProtectedRows<TRow extends { entityId?: string }>(
  seedRows: readonly TRow[],
  baseline: readonly TRow[] | undefined,
  protectedIds: ReadonlySet<string>
): TRow[] {
  if (protectedIds.size === 0) return [];
  const protectedSeedRows = seedRows.filter((row) => isProtectedRow(row, protectedIds));
  if (!baseline || baseline.length === 0) return protectedSeedRows;
  const baselineById = new Map<string, TRow>();
  for (const row of baseline) {
    if (isProtectedRow(row, protectedIds)) baselineById.set(row.entityId, row);
  }
  return protectedSeedRows.map((row) => baselineById.get(row.entityId) ?? row);
}

/**
 * The ONE effective-rows rule of an anchor step: the draft when one exists,
 * the seed view on an untouched done step (a cleared table stays empty — its
 * rows are pending removals), plus the protected rows, which are always
 * present even in an empty draft. What the table displays, what Weiter
 * submits, and what the Weiter-enable gate counts must all be THIS —
 * hand-copied variants are exactly the drift that wedged the solo pre-placed
 * admin (a stamping path unreachable behind a raw-draft-count gate).
 */
export function effectiveAnchorRows<TRow extends { entityId?: string; rowNumber: number }>(config: {
  draftRows: TRow[];
  seedRows: TRow[];
  protectedIds: ReadonlySet<string>;
  isDone: boolean;
  baseline: TRow[] | undefined;
}): TRow[] {
  const { draftRows, seedRows, protectedIds, isDone, baseline } = config;
  const protectedRows = resolveProtectedRows(seedRows, baseline, protectedIds);
  const untouchedDone =
    draftRows.length === 0 && isDone && !isClearedAnchorDraft(draftRows, baseline, protectedIds);
  // The untouched-done adoption takes replaceable rows from the seeds only;
  // protected rows always come from the baseline-aware resolve above.
  const baseRows = untouchedDone
    ? seedRows.filter((row) => !isProtectedRow(row, protectedIds))
    : draftRows;
  return withProtectedRows(baseRows, protectedRows);
}

/**
 * Maps changed protected rows to placement PATCH payloads. Strictly the
 * placement fields — the request schema is a strictObject with no identity
 * fields by design. The department travels as the draft's stable id, NOT the
 * current abbreviation: the backend lookup resolves ids directly, and the id
 * cannot go stale when someone renames the department mid-session.
 */
export function buildPlacementRequests(
  rows: ReadonlyArray<ParsedUser & { entityId: string }>
): Array<{ id: string; data: InitializeAssignUserPlacementRequest }> {
  return rows.map((row) => ({
    id: row.entityId,
    data: {
      department: row.data.department,
      ...(row.data.role && { role: row.data.role }),
      ...(row.data.roleDescription && { roleDescription: row.data.roleDescription })
    }
  }));
}

/**
 * Commits changed protected rows one placement PATCH at a time. The backend's
 * unique {name, kind} OrgFunction index now backstops the concurrent
 * find-or-create race that used to mint duplicate Stellen; the commit stays
 * sequential anyway — one PATCH at a time keeps error attribution per row and
 * avoids surfacing raw duplicate-key errors the index would throw under
 * parallel requests (see role-org-function-maintenance.ts).
 */
export async function commitPlacementRows(
  rows: ReadonlyArray<ParsedUser & { entityId: string }>,
  mutate: (patch: { id: string; data: InitializeAssignUserPlacementRequest }) => Promise<unknown>
): Promise<void> {
  for (const patch of buildPlacementRequests(rows)) {
    await mutate(patch);
  }
}

/**
 * Upload merge rule for the users step: an uploaded row matching a protected
 * row by email applies department/role/roleDescription onto it — identity is
 * identical by definition of the email match, and the locked identity fields
 * must keep their server values. Only NON-EMPTY uploaded placement fields
 * apply: a sheet typically lists the admin without Abteilung/Stelle cells
 * (they didn't manage themselves in it), and blanking an already-committed
 * placement would invalidate the row and block Weiter for data that is still
 * intact server-side. Validity is re-derived from the merged data — the
 * uploaded row's own errors may complain about cells the merge just filled.
 */
export function mergeUploadedUserRow(protectedIds: ReadonlySet<string>) {
  return (current: ParsedUser, uploaded: ParsedUser): ParsedUser => {
    if (!isProtectedRow(current, protectedIds)) {
      return { ...uploaded, entityId: current.entityId };
    }
    const mergedData = {
      ...current.data,
      ...(uploaded.data.department && { department: uploaded.data.department }),
      ...(uploaded.data.role && { role: uploaded.data.role }),
      ...(uploaded.data.roleDescription && { roleDescription: uploaded.data.roleDescription })
    };
    const { isValid, errors } = validateUserRow(mergedData);
    return {
      ...uploaded,
      entityId: current.entityId,
      data: mergedData,
      isValid,
      errors
    };
  };
}
